/**
 * Writing the client's answers onto the OFFICIAL form (owner, 3 Oct 2026: "I want the official form
 * to be pre-populated", not a sheet to copy from).
 *
 * The government forms are print-and-fill PDFs: no fields, only drawn boxes. tools/forms/
 * map_official_form.py finds every box from its question number and label and saves the form, plus
 * a JSON map of its boxes, under src/assets/forms. Here the answers are written into those boxes —
 * a cross in a tick box, text in a field, DD / MM / YYYY in a date — and the client gets the real
 * form back, ready to check, complete and sign.
 *
 * What is written is only what the profile knows. Anything it does not (a reference number, an
 * address, a legal description) is left for the client, and the form's own declaration is never
 * touched. Services Australia publishes the forms under Creative Commons Attribution 4.0.
 */
import { PDFButton, PDFDocument, PDFName, PDFNumber, PDFTextField, StandardFonts, rgb, type PDFPage } from 'pdf-lib';

export interface FormTemplate {
    code: string;
    edition: string;
    pageSize: [number, number];
    source: string;
    fields: Record<string, { kind: 'tick' | 'text' | 'date' | 'comb'; page: number; boxes: number[][]; padLeft?: number; cells?: number; perAt?: number }>;
}

/** true = tick the box; a string = write it (a date as YYYY-MM-DD); absent/false = leave blank. */
export type FormValues = Record<string, string | boolean | null | undefined>;

const INK = rgb(0.05, 0.09, 0.32);

/** The standard PDF fonts only cover Latin-1: swap the characters that would otherwise throw. */
function safe(text: string): string {
    return text
        .replace(/[‘’]/g, "'")
        .replace(/[“”]/g, '"')
        .replace(/[–—]/g, '-')
        .replace(/…/g, '...')
        .replace(/[^\x20-\x7E\xA0-\xFF]/g, '');
}

/**
 * Fill a print-only official form AS AN EDITABLE FORM (owner, 5 Oct 2026: "if you made an error and
 * I want to change a cross or a value, it looks like you can't").
 *
 * The SA485, SA457 and SA456 come with no fillable fields: only printed boxes. The answers used to be
 * drawn onto the page as ink, which nobody could change. Now every box the map knows becomes a real
 * field laid over the printed one — a tick box, a text box, one box per date part, a comb for a phone
 * number — with the profile's answer already in it. The client can untick, overwrite, and type into
 * the ones left blank, then print. Boxes the map does not know (and the signature) are left as
 * printed, to complete by hand.
 */
export async function fillOfficialForm(pdfBytes: ArrayBuffer | Uint8Array, template: FormTemplate, values: FormValues): Promise<Uint8Array> {
    const pdf = await PDFDocument.load(pdfBytes);
    const font = await pdf.embedFont(StandardFonts.Helvetica);
    const form = pdf.getForm();
    const pages = pdf.getPages();
    const plain = { borderWidth: 0, textColor: INK, font } as const;

    /** A text field over [x0, y0, x1, y1] (top-left coordinates, as mapped), holding `text`. */
    const textField = (name: string, page: PDFPage, box: number[], text: string | undefined, opts: { comb?: number; padLeft?: number } = {}) => {
        const height = page.getHeight();
        const x0 = box[0]! + (opts.padLeft ?? 0), y0 = box[1]!, x1 = box[2]!, y1 = box[3]!;
        const w = Math.max(4, x1 - x0), h = Math.max(4, y1 - y0);
        const f = form.createTextField(name);
        if (opts.comb) { f.setMaxLength(opts.comb); f.enableCombing(); }
        const t = text ? safe(text) : '';
        if (t) f.setText(opts.comb ? t.slice(0, opts.comb) : t);
        f.addToPage(page, { ...plain, x: x0, y: height - y1, width: w, height: h });
        let size = Math.min(10, h * 0.62);
        if (!opts.comb) while (t && size > 5 && font.widthOfTextAtSize(t, size) > w - 4) size -= 0.5;
        f.setFontSize(size);
    };

    for (const [key, field] of Object.entries(template.fields)) {
        const page = pages[field.page];
        if (!page || !field.boxes.length) continue;
        const raw = values[key];
        const value = raw === undefined || raw === null || raw === false || raw === '' ? undefined : raw;
        const height = page.getHeight();
        if (field.kind === 'tick') {
            const [x0, y0, x1, y1] = field.boxes[0]!;
            const cb = form.createCheckBox(key);
            cb.addToPage(page, { ...plain, x: x0!, y: height - y1!, width: x1! - x0!, height: y1! - y0! });
            if (value === true) cb.check();
        } else if (field.kind === 'date') {
            // DD, MM and YYYY each get their own box, as printed.
            const m = typeof value === 'string' ? /^(\d{4})-(\d{2})-(\d{2})/.exec(value) : null;
            const parts = m ? [m[3]!, m[2]!, m[1]!] : [];
            field.boxes.forEach((box, i) => textField(`${key}_${['d', 'm', 'y'][i] ?? i}`, page, box, parts[i], { comb: i === 2 ? 4 : 2 }));
        } else if (field.kind === 'comb') {
            const text = typeof value === 'string' ? value.replace(/\s+/g, '') : undefined;
            textField(key, page, field.boxes[0]!, text, { comb: Math.max(field.cells ?? 10, 1) });
        } else if (field.perAt) {
            // "$ amount per period": the amount and the period each get their own part of the box.
            const [amount, period] = typeof value === 'string' && / per /.test(value) ? value.split(' per ') : [typeof value === 'string' ? value : undefined, undefined];
            const [x0, y0, x1, y1] = field.boxes[0]!;
            textField(key, page, [x0!, y0!, x0! + field.perAt - 14, y1!], amount, { padLeft: field.padLeft });
            textField(`${key}_per`, page, [x0! + field.perAt - 4, y0!, x1!, y1!], period);
        } else {
            textField(key, page, field.boxes[0]!, typeof value === 'string' ? value : undefined, { padLeft: field.padLeft });
        }
    }
    form.updateFieldAppearances(font);
    pdf.setTitle(`${template.code} — pre-filled`);
    pdf.setSubject(`${template.edition}, pre-filled from the client profile. Source: ${template.source} (CC BY 4.0, Services Australia).`);
    return pdf.save();
}

/** Opens the filled form in a new tab and downloads a copy. */
export function openFilledPdf(bytes: Uint8Array, fileName: string): void {
    const blob = new Blob([bytes as BlobPart], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener');
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 60000);
}

/**
 * A form that HAS fillable fields (the ATO's NAT 75073, Services Australia's SA002 and SA369): set
 * them by name and keep the form editable, so the client can still type in what the profile does
 * not hold.
 *
 * A text field takes a string. A tick box takes `true`, or — where several boxes share one name, as
 * the Yes / No pairs on the Services Australia forms do — the printed option to tick ("Yes", "No",
 * "Female"). A name or option a new edition no longer has is skipped, never guessed.
 */
export async function fillAcroForm(pdfBytes: ArrayBuffer | Uint8Array, values: Record<string, string | boolean | null | undefined>, meta: { title: string; subject: string }, opts: { revealHidden?: boolean } = {}): Promise<Uint8Array> {
    const pdf = await PDFDocument.load(pdfBytes);
    const form = pdf.getForm();
    const font = await pdf.embedFont(StandardFonts.Helvetica);
    if (opts.revealHidden) revealAnswerFields(form);
    for (const [name, value] of Object.entries(values)) {
        if (value === null || value === undefined || value === false || value === '') continue;
        let field;
        try {
            field = form.getField(name);
        } catch {
            continue;
        }
        if (field instanceof PDFTextField) {
            if (typeof value !== 'string') continue;
            const max = field.getMaxLength();
            field.setText(max ? safe(value).slice(0, max) : safe(value));
            continue;
        }
        tickOption(field.acroField, value);
    }
    form.updateFieldAppearances(font);
    pdf.setTitle(meta.title);
    pdf.setSubject(meta.subject);
    return pdf.save();
}

/**
 * The Services Australia claim forms hide most of their fields until the form's own scripts reveal
 * them — the whole partner column waits for "Do you have a partner? Yes". A value written into a
 * hidden field is there but cannot be seen, printed or typed over, and not every reader runs the
 * scripts. So every answer field is shown, as the printed boxes beneath it already are. The form's
 * own helpers (its "DummyCalc" fields and "Go to" buttons) stay hidden.
 */
function revealAnswerFields(form: ReturnType<PDFDocument['getForm']>): void {
    const HIDDEN = 1 << 1;
    for (const field of form.getFields()) {
        if (field instanceof PDFButton || field.getName().startsWith('DummyCalc')) continue;
        for (const w of field.acroField.getWidgets()) {
            const flags = w.dict.get(PDFName.of('F'));
            const n = flags instanceof PDFNumber ? flags.asNumber() : 0;
            if (n & HIDDEN) w.dict.set(PDFName.of('F'), PDFNumber.of(n & ~HIDDEN));
        }
    }
}

/** Tick the box whose "on" name is `option` (or the only box, for `true`); every other box of the field is cleared. */
function tickOption(acroField: { getWidgets(): Array<{ getOnValue(): PDFName | undefined; setAppearanceState(s: PDFName): void }>; dict: { set(k: PDFName, v: PDFName): void } }, option: string | true): boolean {
    const widgets = acroField.getWidgets();
    const target = widgets.find((w) => {
        const on = w.getOnValue();
        return !!on && (option === true || on.decodeText() === option);
    });
    if (!target) return false;
    const on = target.getOnValue()!;
    for (const w of widgets) w.setAppearanceState(w === target ? on : PDFName.of('Off'));
    acroField.dict.set(PDFName.of('V'), on);
    return true;
}

/** The names and tick options a fillable form actually has: what the tests hold the value builders to. */
export async function acroFieldOptions(pdfBytes: ArrayBuffer | Uint8Array): Promise<Map<string, Set<string> | 'text'>> {
    const pdf = await PDFDocument.load(pdfBytes);
    const out = new Map<string, Set<string> | 'text'>();
    for (const f of pdf.getForm().getFields()) {
        if (f instanceof PDFTextField) { out.set(f.getName(), 'text'); continue; }
        const opts = new Set<string>();
        for (const w of f.acroField.getWidgets()) { const on = w.getOnValue(); if (on) opts.add(on.decodeText()); }
        out.set(f.getName(), opts);
    }
    return out;
}
